import {
  type BlobRecord,
  BlobStorageBase,
  type ListedBlobRecord,
} from '../../storage';
import { HttpConnection } from '../cloud/http';

export interface PipeBlobStorageOptions {
  id: string;
  serverBaseUrl: string;
}

interface ListedBlobJson {
  key: string;
  mime: string;
  size: number;
  createdAt?: string;
}

/**
 * Remote blob storage over the Core API REST surface (`/api/workspaces/:id/blobs[/:key]`), which
 * brokers MinIO. Every mutation is audited by the Core API per non-negotiable #3 — blobs never touch
 * the CRDT sync server. Requests are same-origin and carry the session cookie (workspace-membership
 * authorized server-side).
 */
export class PipeBlobStorage extends BlobStorageBase {
  static readonly identifier = 'PipeBlobStorage';
  override readonly isReadonly = false;

  readonly connection = new HttpConnection(this.options.serverBaseUrl);

  constructor(private readonly options: PipeBlobStorageOptions) {
    super();
  }

  private blobUrl(key: string): string {
    return `/api/workspaces/${this.options.id}/blobs/${encodeURIComponent(key)}`;
  }

  override async get(key: string, signal?: AbortSignal): Promise<BlobRecord | null> {
    const res = await this.connection.fetch(this.blobUrl(key), {
      cache: 'default',
      signal,
    });
    if (res.status === 404) {
      return null;
    }
    const blob = await res.blob();
    return {
      key,
      data: new Uint8Array(await blob.arrayBuffer()),
      mime: blob.type,
      createdAt: new Date(res.headers.get('last-modified') ?? Date.now()),
    };
  }

  override async set(blob: BlobRecord, signal?: AbortSignal): Promise<void> {
    await this.connection.fetch(this.blobUrl(blob.key), {
      method: 'PUT',
      body: blob.data as BodyInit,
      headers: { 'content-type': blob.mime || 'application/octet-stream' },
      signal,
    });
  }

  override async delete(
    key: string,
    _permanently: boolean,
    signal?: AbortSignal
  ): Promise<void> {
    // The Core API hard-deletes (audited); there is no soft-delete tier, so `permanently` is moot.
    await this.connection.fetch(this.blobUrl(key), { method: 'DELETE', signal });
  }

  override async release(_signal?: AbortSignal): Promise<void> {
    // No soft-deleted tier to garbage-collect.
    return;
  }

  override async list(signal?: AbortSignal): Promise<ListedBlobRecord[]> {
    const res = await this.connection.fetch(
      `/api/workspaces/${this.options.id}/blobs`,
      { signal }
    );
    const rows = (await res.json()) as ListedBlobJson[];
    return rows.map(row => ({
      key: row.key,
      mime: row.mime,
      size: row.size,
      createdAt: row.createdAt ? new Date(row.createdAt) : undefined,
    }));
  }
}
